import axios from 'axios';

const api = axios.create({
    baseURL: process.env.NEXT_PUBLIC_API_URL,
    withCredentials: true, // sends httpOnly cookie automatically
});

export default api;

/** Resolve a /uploads/... path to the full backend URL. */
export function resolveUploadUrl(path: string): string {
    if (!path || path.startsWith('data:') || path.startsWith('http') || !path.startsWith('/uploads/')) return path;
    const base = (process.env.NEXT_PUBLIC_API_URL ?? '').replace('/api/v1', '');
    return `${base}${path}`;
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export const authApi = {
    register: (data: { email: string; password: string; name: string }) =>
        api.post('/auth/register', data).then((r) => r.data),

    login: (data: { email: string; password: string }) =>
        api.post('/auth/login', data).then((r) => r.data),

    logout: () => api.post('/auth/logout'),

    me: (): Promise<{
        id: string;
        name: string;
        email: string;
        emailVerified: boolean
    }> =>
        api.get('/auth/me').then((r) => r.data),

    forgotPassword: (data: { email: string }) =>
        api.post('/auth/forgot-password', data).then((r) => r.data),

    resetPassword: (data: { token: string; newPassword: string }) =>
        api.post('/auth/reset-password', data).then((r) => r.data),

    verifyEmail: (data: { token: string }) =>
        api.post('/auth/verify-email', data).then((r) => r.data),

    resendVerification: (data: { email: string }) =>
        api.post('/auth/resend-verification', data).then((r) => r.data),

    updateProfile: (data: {
        name?: string;
        email?: string;
        currentPassword?: string;
        newPassword?: string
    }) =>
        api.patch('/auth/profile', data).then((r) => r.data as {
            id: string;
            name: string;
            email: string;
            emailVerified: boolean
        }),

    completeOnboarding: () => api.post('/auth/complete-onboarding'),
};

// ── App Store ─────────────────────────────────────────────────────────────────

export interface AppStoreScreenshotCard {
    imageData: string;
    width: number;
    height: number;
    cardName: string;
}

export interface AppStoreUploadScreenshotsPayload {
    appleLocalizationId: string;
    deleteExisting: boolean;
    cards: AppStoreScreenshotCard[];
}

export interface UploadScreenshotResult {
    cardName: string;
    status: 'success' | 'error';
    message?: string;
}

export interface PlayStoreScreenshotCard {
    imageData: string;
    width: number;
    height: number;
    cardName: string;
}

export interface PlayStoreUploadScreenshotsPayload {
    packageName: string;
    language: string;
    cards: PlayStoreScreenshotCard[];
}

export interface AppStoreAppAttributes {
    name: string | null;
    bundleId: string | null;
    iconUrl?: string;
}

export interface AppStoreApp {
    id: string;
    attributes: AppStoreAppAttributes;
}

export interface AppStoreVersionAttributes {
    versionString: string;
    platform: string;
    appStoreState: string;
}

export interface AppStoreVersion {
    id: string;
    attributes: AppStoreVersionAttributes;
}

export interface AppStoreLocalizationAttributes {
    locale: string;
    description: string;
    keywords: string;
    marketingUrl: string;
    promotionalText: string;
    whatsNew: string;
}

export interface AppStoreLocalization {
    id: string;
    attributes: AppStoreLocalizationAttributes;
}

export interface AppStoreAppsResponse {
    data: AppStoreApp[];
}

export interface AppStoreVersionsResponse {
    data: AppStoreVersion[];
}

export interface AppStoreLocalizationsResponse {
    data: AppStoreLocalization[];
}

export interface PlayStoreApp {
    packageName: string;
    title: string | null;
    rawData?: { iconUrl?: string | null };
}

export interface PlayStoreListing {
    language: string;
}

export const appStoreApi = {
    getApps: (wsId: string): Promise<AppStoreAppsResponse> =>
        api.get(`/workspaces/${wsId}/appstore/apps`).then((r) => r.data),

    registerApp: (wsId: string, appleAppId: string) =>
        api.post(`/workspaces/${wsId}/appstore/apps`, {appleAppId}).then((r) => r.data),

    deleteApp: (wsId: string, appleAppId: string) =>
        api.delete(`/workspaces/${wsId}/appstore/apps/${appleAppId}`),

    getVersions: (wsId: string, appleAppId: string): Promise<AppStoreVersionsResponse> =>
        api.get(`/workspaces/${wsId}/appstore/apps/${appleAppId}/versions`).then((r) => r.data),

    getVersionLocalizations: (wsId: string, appleVersionId: string): Promise<AppStoreLocalizationsResponse> =>
        api.get(`/workspaces/${wsId}/appstore/versions/${appleVersionId}/localizations`).then((r) => r.data),

    createVersionLocalization: (wsId: string, appleVersionId: string, locale: string) =>
        api.post(`/workspaces/${wsId}/appstore/versions/${appleVersionId}/localizations`, {locale}).then((r) => r.data),

    updateVersionLocalization: (wsId: string, locId: string, data: object, isFirstPublish?: boolean) =>
        api.patch(`/workspaces/${wsId}/appstore/version-localizations/${locId}`, data, {params: {isFirstPublish}}).then((r) => r.data),

    createVersion: (wsId: string, appleAppId: string, data: object) =>
        api.post(`/workspaces/${wsId}/appstore/apps/${appleAppId}/versions`, data).then((r) => r.data),

    getReviews: (wsId: string, appleAppId: string, cursor?: string) =>
        api.get(`/workspaces/${wsId}/appstore/apps/${appleAppId}/reviews`, {params: {cursor}}).then((r) => r.data),

    getReviewResponse: (wsId: string, reviewId: string) =>
        api.get(`/workspaces/${wsId}/appstore/reviews/${reviewId}/response`).then((r) => r.data),

    postReviewResponse: (wsId: string, reviewId: string, responseBody: string) =>
        api.post(`/workspaces/${wsId}/appstore/reviews/${reviewId}/response`, {responseBody}).then((r) => r.data),

    getAppInfo: (wsId: string, appleAppId: string) =>
        api.get(`/workspaces/${wsId}/appstore/apps/${appleAppId}/info`).then((r) => r.data),

    getAppInfoLocalizations: (wsId: string, appleInfoId: string) =>
        api.get(`/workspaces/${wsId}/appstore/app-infos/${appleInfoId}/localizations`).then((r) => r.data),

    updateAppInfoLocalization: (wsId: string, locId: string, data: object) =>
        api.patch(`/workspaces/${wsId}/appstore/app-info-localizations/${locId}`, data).then((r) => r.data),

    uploadScreenshots: (wsId: string, appleLocalizationId: string, data: AppStoreUploadScreenshotsPayload): Promise<{
        results: UploadScreenshotResult[]
    }> =>
        api.post(`/workspaces/${wsId}/appstore/version-localizations/${appleLocalizationId}/screenshots/upload`, data).then((r) => r.data),
};

// ── Notifications ─────────────────────────────────────────────────────────────

export const notificationsApi = {
    list: (wsId: string) =>
        api.get(`/workspaces/${wsId}/notifications`).then((r) => r.data),

    create: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/notifications`, data).then((r) => r.data),

    update: (wsId: string, id: string, data: object) =>
        api.patch(`/workspaces/${wsId}/notifications/${id}`, data).then((r) => r.data),

    delete: (wsId: string, id: string) =>
        api.delete(`/workspaces/${wsId}/notifications/${id}`),

    send: (wsId: string, id: string, data: object) =>
        api.post(`/workspaces/${wsId}/notifications/${id}/send`, data).then((r) => r.data),

    history: (wsId: string, id: string) =>
        api.get(`/workspaces/${wsId}/notifications/${id}/history`).then((r) => r.data),
};

// ── Screenshots ───────────────────────────────────────────────────────────────

import {type SaveScreenshotConfigDto, type ScreenshotConfig} from '../designTemplates';

export const screenshotsApi = {
    list: (wsId: string): Promise<ScreenshotConfig[]> =>
        api.get(`/workspaces/${wsId}/screenshots`).then((r) => r.data),

    create: (wsId: string, data: SaveScreenshotConfigDto): Promise<ScreenshotConfig> =>
        api.post(`/workspaces/${wsId}/screenshots`, data).then((r) => r.data),

    update: (wsId: string, id: string, data: SaveScreenshotConfigDto): Promise<ScreenshotConfig> =>
        api.patch(`/workspaces/${wsId}/screenshots/${id}`, data).then((r) => r.data),

    delete: (wsId: string, id: string) =>
        api.delete(`/workspaces/${wsId}/screenshots/${id}`),

    export: (wsId: string, id: string, data: object) =>
        api.post(`/workspaces/${wsId}/screenshots/${id}/export`, data).then((r) => r.data),
};

// ── Icon Builder ──────────────────────────────────────────────────────────────

export const iconBuilderApi = {
    list: (wsId: string) =>
        api.get(`/workspaces/${wsId}/icon-builder`).then((r) => r.data),

    create: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/icon-builder`, data).then((r) => r.data),

    delete: (wsId: string, id: string) =>
        api.delete(`/workspaces/${wsId}/icon-builder/${id}`).then((r) => r.data),

    uploadSource: (wsId: string, id: string, file: File) => {
        const form = new FormData();
        form.append('file', file);
        return api.post(`/workspaces/${wsId}/icon-builder/${id}/source`, form).then((r) => r.data);
    },

    export: (wsId: string, id: string) =>
        api.post(`/workspaces/${wsId}/icon-builder/${id}/export`).then((r) => r.data),
};

// ── Media Library ─────────────────────────────────────────────────────────────

export const mediaApi = {
    list: (wsId: string): Promise<{
        items: { url: string; filename: string }[]
    }> =>
        api.get(`/workspaces/${wsId}/media`).then((r) => r.data),

    upload: (wsId: string, file: File): Promise<{
        url: string;
        filename: string
    }> => {
        const form = new FormData();
        form.append('file', file);
        return api.post(`/workspaces/${wsId}/media/upload`, form).then((r) => r.data);
    },

    delete: (wsId: string, filename: string): Promise<void> =>
        api.delete(`/workspaces/${wsId}/media/${filename}`).then(() => undefined),
};

// ── Credentials ───────────────────────────────────────────────────────────────

export interface AppStoreCredential {
    id: string;
    name: string;
    keyId: string;
    issuerId: string;
    isActive: boolean;
    createdAt: string;
}

export interface PlayStoreCredential {
    id: string;
    displayName: string;
    serviceAccountEmail: string;
    isActive: boolean;
    createdAt: string;
}

export const credentialsApi = {
    listAppStore: (wsId: string) =>
        api.get(`/workspaces/${wsId}/credentials/appstore`).then((r) => r.data as AppStoreCredential[]),

    createAppStore: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/credentials/appstore`, data).then((r) => r.data as AppStoreCredential),

    updateAppStore: (wsId: string, id: string, data: object) =>
        api.patch(`/workspaces/${wsId}/credentials/appstore/${id}`, data).then((r) => r.data as AppStoreCredential),

    activateAppStore: (wsId: string, id: string) =>
        api.post(`/workspaces/${wsId}/credentials/appstore/${id}/activate`).then((r) => r.data as AppStoreCredential),

    deleteAppStore: (wsId: string, id: string) =>
        api.delete(`/workspaces/${wsId}/credentials/appstore/${id}`),

    listFirebase: (wsId: string) =>
        api.get(`/workspaces/${wsId}/credentials/firebase`).then((r) => r.data),

    createFirebase: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/credentials/firebase`, data).then((r) => r.data),

    deleteFirebase: (wsId: string, id: string) =>
        api.delete(`/workspaces/${wsId}/credentials/firebase/${id}`),

    listPlayStore: (wsId: string) =>
        api.get(`/workspaces/${wsId}/credentials/playstore`).then((r) => r.data as PlayStoreCredential[]),

    createPlayStore: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/credentials/playstore`, data).then((r) => r.data as PlayStoreCredential),

    activatePlayStore: (wsId: string, id: string) =>
        api.post(`/workspaces/${wsId}/credentials/playstore/${id}/activate`).then((r) => r.data as PlayStoreCredential),

    updatePlayStore: (wsId: string, id: string, data: object) =>
        api.patch(`/workspaces/${wsId}/credentials/playstore/${id}`, data).then((r) => r.data as PlayStoreCredential),

    deletePlayStore: (wsId: string, id: string) =>
        api.delete(`/workspaces/${wsId}/credentials/playstore/${id}`),
};

// ── Play Store ────────────────────────────────────────────────────────────────

export const playStoreApi = {
    getApps: (wsId: string): Promise<PlayStoreApp[]> =>
        api.get(`/workspaces/${wsId}/playstore/apps`).then((r) => r.data),

    registerApp: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/playstore/apps`, data).then((r) => r.data),

    deleteApp: (wsId: string, id: string) =>
        api.delete(`/workspaces/${wsId}/playstore/apps/${id}`),

    getListings: (wsId: string, packageName: string): Promise<PlayStoreListing[]> =>
        api.get(`/workspaces/${wsId}/playstore/apps/${packageName}/listings`).then((r) => r.data),

    addListing: (wsId: string, packageName: string, language: string) =>
        api.post(`/workspaces/${wsId}/playstore/apps/${packageName}/listings/${language}`, {}).then((r) => r.data),

    updateListing: (wsId: string, packageName: string, language: string, data: object) =>
        api.patch(`/workspaces/${wsId}/playstore/apps/${packageName}/listings/${language}`, data).then((r) => r.data),

    batchUpdateListings: (wsId: string, packageName: string, listings: {
        language: string;
        [key: string]: any
    }[]) =>
        api.post(`/workspaces/${wsId}/playstore/apps/${packageName}/listings/batch-update`, {listings}).then((r) => r.data),

    getDetails: (wsId: string, packageName: string) =>
        api.get(`/workspaces/${wsId}/playstore/apps/${packageName}/details`).then((r) => r.data),

    getReviews: (wsId: string, packageName: string, token?: string) =>
        api.get(`/workspaces/${wsId}/playstore/apps/${packageName}/reviews`, {params: {token}}).then((r) => r.data),

    iconProxyUrl: (wsId: string, iconUrl: string) =>
        `${process.env.NEXT_PUBLIC_API_URL}/workspaces/${wsId}/playstore/icon-proxy?url=${encodeURIComponent(iconUrl)}`,

    replyToReview: (wsId: string, packageName: string, reviewId: string, data: object) =>
        api.post(`/workspaces/${wsId}/playstore/apps/${packageName}/reviews/${reviewId}/reply`, data).then((r) => r.data),

    uploadScreenshots: (wsId: string, packageName: string, data: PlayStoreUploadScreenshotsPayload): Promise<{
        results: UploadScreenshotResult[]
    }> =>
        api.post(`/workspaces/${wsId}/playstore/apps/${packageName}/screenshots/upload`, data).then((r) => r.data),
};

// ── App description helper ────────────────────────────────────────────────────

export type AppRef =
    | { store: 'appstore'; appId: string }
    | { store: 'playstore'; packageName: string };

/** Fetches the best available English description for a registered app. */
export async function fetchAppDescription(wsId: string, ref: AppRef): Promise<string> {
    if (ref.store === 'appstore') {
        const versions = await appStoreApi.getVersions(wsId, ref.appId);
        const versionId = versions?.data?.[0]?.id;
        if (!versionId) return '';
        const locs = await appStoreApi.getVersionLocalizations(wsId, versionId);
        const list = locs?.data ?? [];
        const enLoc = list.find((l) => l.attributes.locale === 'en-US') ?? list[0];
        return enLoc?.attributes?.description ?? '';
    } else {
        const listings: {
            language: string;
            shortDescription?: string;
            fullDescription?: string
        }[] =
            await playStoreApi.getListings(wsId, ref.packageName);
        const enListing = listings.find((l) => l.language === 'en-US') ?? listings[0];
        return enListing?.fullDescription ?? enListing?.shortDescription ?? '';
    }
}

// ── AI ────────────────────────────────────────────────────────────────────────

export const aiApi = {
    prompt: (wsId: string, text: string) =>
        api.post(`/workspaces/${wsId}/ai/prompt`, {prompt: text}).then((r) => r.data.content),

    translate: (wsId: string, text: string, targetLocale: string) =>
        api.post(`/workspaces/${wsId}/ai/translate`, {
            text,
            targetLocale
        }).then((r) => r.data.translation),

    translateBatch: (wsId: string, texts: string[], targetLocale: string) =>
        api.post(`/workspaces/${wsId}/ai/translate-batch`, {
            texts,
            targetLocale
        }).then((r) => r.data.translations as string[]),

    translateBatchMultiLocale: (wsId: string, texts: string[], targetLocales: string[]) =>
        api.post(`/workspaces/${wsId}/ai/translate-batch-multi-locale`, {
            texts,
            targetLocales,
        }).then((r) => r.data.results as Record<string, string[]>),
};

// ── AI Store ──────────────────────────────────────────────────────────────────

export const aiStoreApi = {
    reviewReply: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-store/review-reply`, data).then((r) => r.data as {
            reply: string
        }),

    improveDescription: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-store/improve-description`, data).then((r) => r.data as {
            description: string
        }),

    asoScore: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-store/aso-score`, data).then((r) => r.data as {
            score: number;
            feedback: string
        }),

    improveWhatsNew: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-store/improve-whats-new`, data).then((r) => r.data as {
            whatsNew: string
        }),

    improveKeywords: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-store/improve-keywords`, data).then((r) => r.data as {
            keywords: string
        }),

    improveShortDescription: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-store/improve-short-desc`, data).then((r) => r.data as {
            shortDescription: string
        }),
};

// ── Billing ───────────────────────────────────────────────────────────────────

export const billingApi = {
    getUsage: (wsId: string) =>
        api.get(`/workspaces/${wsId}/billing/usage`).then((r) => r.data),

    createCheckout: (wsId: string, priceId: string) =>
        api.post(`/workspaces/${wsId}/billing/checkout`, {priceId}).then((r) => r.data as {
            url: string
        }),

    createPortal: (wsId: string) =>
        api.post(`/workspaces/${wsId}/billing/portal`).then((r) => r.data as {
            url: string
        }),
};

// ── Newsletter ────────────────────────────────────────────────────────────────

export const newsletterApi = {
    subscribe: (email: string) =>
        api.post('/newsletter/subscribe', {email}).then((r) => r.data),
};

// ── Analytics ─────────────────────────────────────────────────────────────────

export const analyticsApi = {
    getAppIds: (wsId: string) =>
        api.get(`/workspaces/${wsId}/analytics/appids`).then((r) => r.data as string[]),

    getDailyVolume: (wsId: string, appid: string, from?: string, to?: string, event?: string) =>
        api.get(`/workspaces/${wsId}/analytics/${appid}/daily-volume`, {
            params: {
                from,
                to,
                event
            }
        }).then((r) => r.data as { day: string; count: number }[]),

    getEventBreakdown: (wsId: string, appid: string, from?: string, to?: string) =>
        api.get(`/workspaces/${wsId}/analytics/${appid}/event-breakdown`, {
            params: {
                from,
                to
            }
        }).then((r) => r.data as { event: string; count: number }[]),

    getTopParams: (wsId: string, appid: string, event: string, from?: string, to?: string) =>
        api.get(`/workspaces/${wsId}/analytics/${appid}/top-params`, {
            params: {
                event,
                from,
                to
            }
        }).then((r) => r.data as { param: string; count: number }[]),

    getHourlyHeatmap: (wsId: string, appid: string, from?: string, to?: string) =>
        api.get(`/workspaces/${wsId}/analytics/${appid}/hourly-heatmap`, {
            params: {
                from,
                to
            }
        }).then((r) => r.data as {
            hour: number;
            dow: number;
            count: number
        }[]),

    getWorkspaceOverview: (wsId: string, from?: string, to?: string) =>
        api.get(`/workspaces/${wsId}/analytics/workspace-overview`, {
            params: {
                from,
                to
            }
        }).then((r) => r.data as {
            appid: string;
            event: string;
            count: number
        }[]),

    getSessions: (wsId: string, appid: string, from?: string, to?: string) =>
        api.get(`/workspaces/${wsId}/analytics/${appid}/sessions`, {
            params: {
                from,
                to
            }
        }).then((r) => r.data as {
            day: string;
            new_sessions: number;
            returning_sessions: number
        }[]),

    getFunnel: (wsId: string, appid: string, events: string[], from?: string, to?: string) =>
        api.get(`/workspaces/${wsId}/analytics/${appid}/funnel`, {
            params: {
                events: events.join(','),
                from,
                to
            }
        }).then((r) => r.data as {
            step: number;
            event: string;
            count: number
        }[]),

    getRollingAverage: (wsId: string, appid: string, from?: string, to?: string) =>
        api.get(`/workspaces/${wsId}/analytics/${appid}/rolling-average`, {
            params: {
                from,
                to
            }
        }).then((r) => r.data as {
            day: string;
            count: number;
            rolling_avg: number
        }[]),

    getTopData: (wsId: string, appid: string, event: string, from?: string, to?: string) =>
        api.get(`/workspaces/${wsId}/analytics/${appid}/top-data`, {
            params: {
                event,
                from,
                to
            }
        }).then((r) => r.data as { data: string; count: number }[]),

    getVelocity: (wsId: string, appid: string, from?: string, to?: string) =>
        api.get(`/workspaces/${wsId}/analytics/${appid}/velocity`, {
            params: {
                from,
                to
            }
        }).then((r) => r.data as {
            event: string;
            current_week: number;
            previous_week: number;
            change_pct: number
        }[]),
};

// ── Analytics Apps ─────────────────────────────────────────────────────────────

export interface AnalyticsApp {
    id: string;
    workspaceId: string;
    appid: string;
    name: string;
    secretCode: string;
    createdAt: string;
}

export const analyticsAppsApi = {
    list: (wsId: string) =>
        api.get(`/workspaces/${wsId}/analytics/apps`).then((r) => r.data as AnalyticsApp[]),
    create: (wsId: string, data: { appid: string; name: string }) =>
        api.post(`/workspaces/${wsId}/analytics/apps`, data).then((r) => r.data as AnalyticsApp),
    delete: (wsId: string, id: string) =>
        api.delete(`/workspaces/${wsId}/analytics/apps/${id}`),
};

// ── Social Media ──────────────────────────────────────────────────────────────

export interface SocialMediaPost {
    id: string;
    workspaceId: string;
    createdBy: string | null;
    tool: string;
    platform: string;
    title: string | null;
    content: string;
    imageUrl: string | null;
    metadata: Record<string, unknown>;
    createdAt: string;
    updatedAt: string;
}

export const socialMediaApi = {
    generatePost: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-marketing/generate/post`, data).then((r) => r.data as {
            content: string
        }),

    generateHooks: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-marketing/generate/hooks`, data).then((r) => r.data as {
            hooks: string[]
        }),

    repurposeContent: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-marketing/generate/repurpose`, data).then(
            (r) => r.data as {
                x: string;
                linkedin: string;
                reddit: string;
                threads: string;
                videoScript: string;
                emailSnippet: string
            },
        ),

    generateVideoScript: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-marketing/generate/video-script`, data).then(
            (r) => r.data as {
                hookLine: string;
                script: string;
                callToAction: string;
                onScreenTextSuggestions: string[]
            },
        ),

    generateImageScript: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-marketing/generate/image-script`, data).then((r) => r.data as {
            prompt: string
        }),

    generateImage: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-marketing/generate/image`, data).then((r) => r.data as {
            imageBase64?: string
        }),

    listPosts: (wsId: string) =>
        api.get(`/workspaces/${wsId}/ai-marketing/posts`).then((r) => r.data as SocialMediaPost[]),

    savePost: (wsId: string, data: object) =>
        api.post(`/workspaces/${wsId}/ai-marketing/posts`, data).then((r) => r.data as SocialMediaPost),

    deletePost: (wsId: string, id: string) =>
        api.delete(`/workspaces/${wsId}/ai-marketing/posts/${id}`),
};

// ── Dashboard ─────────────────────────────────────────────────────────────────

export interface UnrepliedAppStoreReview {
    id: string;
    rating: number | null;
    title: string | null;
    body: string | null;
    reviewerNickname: string | null;
    createdDate: string | null;
}

export interface UnrepliedPlayStoreReview {
    reviewId: string;
    rating: number | null;
    authorName: string | null;
    text: string | null;
    createTime: string | null;
}

export interface DashboardAppStoreApp {
    id: string;
    appleId: string;
    name: string | null;
    iconUrl: string | null;
    avgRating: number | null;
    totalReviews: number;
    repliedCount: number;
    responseRate: number | null;
    unrepliedReviews: UnrepliedAppStoreReview[];
}

export interface DashboardPlayStoreApp {
    id: string;
    packageName: string;
    title: string | null;
    iconUrl: string | null;
    totalReviews: number;
    avgRating: number | null;
    unrepliedReviews: UnrepliedPlayStoreReview[];
}

export interface DashboardWorkspaceStats {
    totalUnanswered: number;
    mostRecentReviewDate: string | null;
    notificationsSent7d: number;
    screenshotsExported7d: number;
    aiPostsCreated7d: number;
}

export interface DashboardSummary {
    appStoreApps: DashboardAppStoreApp[];
    playStoreApps: DashboardPlayStoreApp[];
    stats: DashboardWorkspaceStats;
}

export interface DashboardActivityEvent {
    type: 'notification' | 'screenshot' | 'icon' | 'social_post';
    timestamp: string;
    description: string;
    icon: 'bell' | 'camera' | 'layers' | 'megaphone';
}

export const dashboardApi = {
    getSummary: (wsId: string): Promise<DashboardSummary> =>
        api.get(`/workspaces/${wsId}/dashboard/summary`).then((r) => r.data),

    getActivity: (wsId: string, days = 7): Promise<DashboardActivityEvent[]> =>
        api.get(`/workspaces/${wsId}/dashboard/activity`, {params: {days}}).then((r) => r.data),
};